import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  X, Download, ExternalLink, RefreshCw, FileText, ImageIcon, FileSpreadsheet,
  FileArchive, Paperclip, Search, Filter, CloudDownload,
} from "lucide-react";

/**
 * ClientFilesModal — one-stop file dashboard for a client.
 * Groups everything by source (Client, Admin, Jotform, Close CRM) with a
 * quick filter + search so Xzavier can grab exactly what he needs.
 */

const SOURCE_META = {
  client:  { label: "Client Upload", color: "bg-sky-100 text-sky-800",     dot: "bg-sky-500"     },
  admin:   { label: "Admin Upload",  color: "bg-slate-100 text-slate-700", dot: "bg-slate-500"   },
  jotform: { label: "Jotform",       color: "bg-amber-100 text-amber-800", dot: "bg-amber-500"   },
  close:   { label: "Close CRM",     color: "bg-violet-100 text-violet-800", dot: "bg-violet-500" },
};

const ICON_FOR = (name, ct) => {
  const lower = (name || "").toLowerCase();
  const type = (ct || "").toLowerCase();
  if (type.startsWith("image") || /\.(png|jpe?g|gif|webp|heic|svg)$/.test(lower)) return ImageIcon;
  if (type.includes("sheet") || /\.(csv|xlsx?|numbers)$/.test(lower))              return FileSpreadsheet;
  if (type.includes("zip") || /\.(zip|rar|7z|tar|gz)$/.test(lower))                return FileArchive;
  return FileText;
};

const fmtSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function ClientFilesModal({ clientId, clientEmail, clientName, onClose }) {
  const [state, setState] = useState({ loading: false, data: null, err: null });
  const [filter, setFilter] = useState("all"); // all | client | admin | jotform | close
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setState({ loading: true, data: null, err: null });
    try {
      const params = clientEmail && clientEmail !== clientId ? { email: clientEmail } : undefined;
      const { data } = await api.get(`/admin/clients/${encodeURIComponent(clientId)}/all-files`, { params });
      setState({ loading: false, data, err: null });
    } catch (e) {
      const err = formatApiError(e.response?.data?.detail) || "Failed to load";
      setState({ loading: false, data: null, err });
      toast.error(err);
    }
  }, [clientId, clientEmail]);
  useEffect(() => { load(); }, [load]);

  const syncJotform = async () => {
    setSyncing(true);
    try {
      const params = clientEmail && clientEmail !== clientId ? { email: clientEmail } : undefined;
      const { data } = await api.post(
        `/admin/clients/${encodeURIComponent(clientId)}/backfill-jotform`,
        null,
        { params },
      );
      if (data.added > 0) {
        toast.success(`Pulled ${data.added} Jotform file${data.added === 1 ? "" : "s"}`);
      } else if (data.matched > 0) {
        toast.info(`Already up to date — ${data.matched} Jotform submission${data.matched === 1 ? "" : "s"} on file, no new uploads`);
      } else {
        toast.info(`No Jotform submissions found for ${data.email || "this client"}`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync failed");
    } finally { setSyncing(false); }
  };

  const filtered = useMemo(() => {
    const files = state.data?.files || [];
    return files
      .filter((f) => filter === "all" || f.source === filter)
      .filter((f) => {
        if (!q.trim()) return true;
        const query = q.trim().toLowerCase();
        return (f.filename || "").toLowerCase().includes(query)
            || (f.content_type || "").toLowerCase().includes(query)
            || (f.uploaded_by_name || "").toLowerCase().includes(query);
      });
  }, [state.data, filter, q]);

  const counts = state.data?.counts || {};
  const total = filtered.length;

  const openFile = async (f) => {
    if (!f.url) { toast.error("No URL"); return; }
    // Files hosted on our proxy (Jotform, Close, admin uploads) need a Bearer + baseURL.
    // Fetch as blob and open the object URL so auth works.
    if (f.url.startsWith("/api/")) {
      try {
        const { data } = await api.get(f.url.replace(/^\/api/, ""), { responseType: "blob" });
        const objUrl = URL.createObjectURL(data);
        const w = window.open(objUrl, "_blank", "noopener");
        if (!w) {
          // Popup blocked — fall back to same-tab navigation
          window.location.href = objUrl;
        }
        setTimeout(() => URL.revokeObjectURL(objUrl), 120_000);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not open file");
      }
      return;
    }
    window.open(f.url, "_blank", "noopener");
  };

  const downloadFile = async (f) => {
    if (!f.url) { toast.error("No URL"); return; }
    try {
      const url = f.url.startsWith("/api/") ? f.url.replace(/^\/api/, "") : f.url;
      const isProxy = f.url.startsWith("/api/");
      const resp = isProxy ? await api.get(url, { responseType: "blob" }) : await fetch(url);
      const blob = isProxy ? resp.data : await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = f.filename || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not download");
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 cq-in" data-testid="client-files-modal">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="relative p-5 sm:p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
          <div className="cq-grain absolute inset-0 opacity-30" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] font-black text-amber-300 flex items-center gap-1.5">
                <Paperclip size={12} /> All Files
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl mt-1">{clientName || "Client"}</h2>
              <p className="text-xs text-slate-300 mt-1">Every file across our platform, Jotform intake, and Close CRM.</p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-300 hover:text-white transition"
              data-testid="client-files-close"
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          {/* Source filter chips */}
          <div className="relative mt-4 flex items-center gap-2 flex-wrap">
            <FilterChip label="All" testid="ff-all" count={(state.data?.files || []).length} active={filter === "all"} onClick={() => setFilter("all")} tone="amber" />
            {Object.entries(SOURCE_META).map(([key, meta]) => (
              <FilterChip
                key={key}
                label={meta.label}
                count={counts[key] || 0}
                active={filter === key}
                onClick={() => setFilter(key)}
                testid={`ff-${key}`}
              />
            ))}
            <button
              onClick={load}
              className="ml-auto inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition"
              data-testid="ff-refresh"
              title="Refresh"
            >
              <RefreshCw size={12} className={state.loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={syncJotform}
              disabled={syncing}
              className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 transition disabled:opacity-60"
              data-testid="ff-sync-jotform"
              title="Pull historical uploads from Jotform for this email"
            >
              <CloudDownload size={12} className={syncing ? "animate-pulse" : ""} /> {syncing ? "Syncing…" : "Sync Jotform"}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-5 sm:px-6 py-3 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search files by name, type, or uploader…"
              className="w-full h-10 rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:border-slate-400"
              data-testid="ff-search"
            />
          </div>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-3 min-h-[240px]">
          {state.loading && <p className="text-sm text-slate-400 text-center py-10">Loading files…</p>}
          {state.err && <p className="text-sm text-rose-600 text-center py-10">{state.err}</p>}
          {!state.loading && !state.err && total === 0 && (
            <div className="text-center py-12">
              <FileText size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 font-bold">
                No files{filter !== "all" ? ` in ${SOURCE_META[filter]?.label}` : ""} yet
              </p>
              <p className="text-xs text-slate-400 mt-2 max-w-md mx-auto leading-relaxed">
                We searched every source for this client:
              </p>
              <ul className="mt-2 inline-flex flex-col text-[11px] text-slate-500 gap-0.5">
                <li>· {counts.client || 0} client-side upload{counts.client === 1 ? "" : "s"}</li>
                <li>· {counts.admin || 0} admin upload{counts.admin === 1 ? "" : "s"}</li>
                <li>· {counts.jotform || 0} Jotform intake attachment{counts.jotform === 1 ? "" : "s"}</li>
                <li>· {counts.close || 0} Close CRM attachment{counts.close === 1 ? "" : "s"} <span className="text-slate-400">(SMS/MMS + Email + Note + links in text)</span></li>
              </ul>
              {state.data?.client?.close_lead_id ? (
                <p className="text-[10px] text-slate-400 mt-3 font-mono">Close lead: {state.data.client.close_lead_id}</p>
              ) : (
                <p className="text-[11px] text-amber-600 mt-3">
                  No Close CRM lead linked to this client yet — attachments there won't sync.
                </p>
              )}
            </div>
          )}
          {!state.loading && !state.err && total > 0 && (
            <ul className="space-y-1.5">
              {filtered.map((f) => {
                const Icon = ICON_FOR(f.filename, f.content_type);
                const meta = SOURCE_META[f.source] || SOURCE_META.client;
                return (
                  <li
                    key={f.id || `${f.filename}-${f.created_at}`}
                    onClick={() => openFile(f)}
                    className="group flex items-center gap-3 rounded-xl border border-slate-100 hover:border-slate-300 bg-white hover:shadow-sm px-3 py-2.5 transition cursor-pointer"
                    data-testid={`ff-row-${f.id}`}
                  >
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-sm text-slate-900 truncate">{f.filename || "Untitled"}</p>
                        <span className={`text-[9px] uppercase tracking-widest font-black rounded-full px-1.5 py-0.5 ${meta.color}`}>{meta.label}</span>
                        {f.channel && <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 border border-slate-200 rounded-full px-1.5 py-0.5">{f.channel}</span>}
                      </div>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {[f.uploaded_by_name, fmtDate(f.created_at), fmtSize(f.size), f.subject].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                      {f.source === "close" && f.close_lead_url ? (
                        <a
                          href={f.close_lead_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center h-8 px-2.5 text-[11px] font-bold rounded-md bg-slate-900 hover:bg-slate-800 text-white"
                          title="Open the parent activity in Close CRM (files require Close login)"
                          data-testid={`ff-open-${f.id}`}
                        >
                          <ExternalLink size={12} className="mr-1" /> View in Close
                        </a>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openFile(f)}
                            className="h-8 px-2.5 text-[11px] font-bold border-slate-200 text-slate-700 hover:text-slate-900 hover:border-slate-400"
                            title="Preview"
                            data-testid={`ff-open-${f.id}`}
                          >
                            <ExternalLink size={12} className="mr-1" /> Preview
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => downloadFile(f)}
                            className="h-8 px-2.5 text-[11px] font-bold bg-slate-900 hover:bg-slate-800 text-white"
                            title="Download"
                            data-testid={`ff-dl-${f.id}`}
                          >
                            <Download size={12} className="mr-1" /> Download
                          </Button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs text-slate-500">
          <span>{total} {total === 1 ? "file" : "files"} shown</span>
          <Button size="sm" variant="outline" onClick={onClose} className="h-8" data-testid="ff-done">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterChip({ label, count, active, onClick, testid, tone }) {
  const activeCls = tone === "amber"
    ? "bg-amber-500 text-white shadow"
    : "bg-white text-slate-900 shadow";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-widest transition ${active ? activeCls : "bg-white/10 text-white/80 hover:bg-white/20"}`}
    >
      {label} {count > 0 && <span className={`inline-flex items-center justify-center rounded-full min-w-4 h-4 px-1 text-[9px] ${active ? "bg-slate-900/10 text-slate-900" : "bg-white/20 text-white"}`}>{count}</span>}
    </button>
  );
}
